/**
 * 需求 R136 — 報價單「重複選到的品項」提醒。
 *
 * PM 2026-08-24 留言逐字拍板：
 *   Q1（擋路）重複品項到底要不要加入？
 *     → 「B＝照樣加入，允許同一產品在清單出現兩列，只是提醒。」
 *   Q5 是否同時在搜尋結果列標示「已在清單中」？
 *     → 「是否建議用此方法就好，等於不另外提醒，版面更乾淨，提醒更直覺。若如此 Q2-Q4 不需回答。」
 *
 * 依此拍板，本案的提醒形式＝**事前**在 Section 2 搜尋結果列直接標示，
 * 不再於「添加到報價單」之後補跳 toast／alert（故 Q2 時機／Q3 形式／Q4 多筆文案皆不需回答，
 * 也不新增 .qc-toast-warning 樣式）。加入行為本身完全不擋、不過濾。
 *
 * 為什麼獨立成一支 utils：
 *   1. 文案「此品項已在報價清單中」必須逐字一致（AC8），兩頁共用同一個常數才不會各改各的。
 *   2. 「哪些產品已在清單中」的判定需要 product id 正規化——編輯頁的 items 來自
 *      GET /quotes/:id，後端 quoteController 對 items.product 做了 populate，
 *      item.product 是**物件**而非字串 id（建立頁則恆為字串）。
 *      舊寫法 `new Set(items.map(i => i.product))` 在編輯頁裝的是物件，
 *      `has(p._id)`（字串）恆為 false → 判定永遠不成立（需求 R136 AC3 的必要前置修正）。
 *      兩頁共用本檔即不可能只修一邊。
 */

/** 兩頁共用的提醒文案，PM 指定逐字，不得改寫（AC8）。 */
export const DUPLICATE_ITEM_HINT = '此品項已在報價清單中';

/**
 * 取得報價單品項的 product id（字串）。
 * populate 後 item.product 是物件 → 取 _id；未 populate 則本身即字串 id。
 * 與兩頁其餘 8 處既有寫法 `typeof item.product === 'object' ? item.product?._id : item.product` 等價。
 */
export function getItemProductId(item) {
  if (!item) return '';
  const raw = typeof item.product === 'object' && item.product !== null
    ? item.product?._id
    : item.product;
  return raw == null ? '' : String(raw);
}

/**
 * 由目前的報價品項清單建出「已在報價清單中的 product id」集合。
 * 供 Section 2 搜尋結果列判斷是否標示 DUPLICATE_ITEM_HINT。
 * 注意：本集合只用於「標示提醒」，不得再拿去過濾要加入的品項（Q1=B 照樣加入）。
 */
export function buildQuotedProductIdSet(items) {
  const set = new Set();
  (items || []).forEach(item => {
    const id = getItemProductId(item);
    if (id) set.add(id);
  });
  return set;
}

/** 該產品是否已在報價清單中（p._id 也一併轉字串，避免型別不一致造成漏判）。 */
export function isProductInQuote(quotedIdSet, product) {
  if (!quotedIdSet || !product || product._id == null) return false;
  return quotedIdSet.has(String(product._id));
}
